// P2-8: central browser-session helper for main-app. The access token now lives
// in an httpOnly cookie the browser sends automatically (withCredentials) — JS
// can neither read nor store it, so an XSS cannot exfiltrate it. Identity comes
// from the gateway's GET /api/v1/auth/session, which also returns a session-bound
// CSRF token echoed (X-CSRF-Token) on every state-changing request.
//
// This replaces the three legacy token systems (localStorage `accessToken` /
// `access_token`, the api-layer TokenStorage, and legacy `amashr_token`).
import type { AxiosRequestConfig } from 'axios';
import { apiConfig } from '@/config/api.config';

export interface SessionUser {
  id: string | null;
  email: string | null;
  tenantId: string | null;
  roles: string[];
  permissions?: string[];
  scope: string | null;
  partnerId: string | null;
  employeeId: string | null;
}

/**
 * Three-way session outcome. `unauthenticated` (401/403 or authenticated:false)
 * is a SETTLED state — the caller should clear auth. `error` (5xx, network
 * failure, aborted request) is INDETERMINATE — the caller must keep the current
 * auth state rather than logging the user out over a transient blip.
 */
export type SessionOutcome =
  | { status: 'authenticated'; user: SessionUser }
  | { status: 'unauthenticated' }
  | { status: 'error' };

// The session-bound CSRF token, held in memory only (never persisted).
let csrf: string | null = null;
export function getCsrf(): string | null {
  return csrf;
}
export function setCsrf(v: string | null): void {
  csrf = v;
}

// Legacy credential keys purged on load — the XSS-exfiltratable tokens this
// migration removes. (Non-credential UI state like tenant_data may remain.)
const LEGACY_KEYS = ['accessToken', 'refreshToken', 'access_token', 'refresh_token', 'amashr_token', 'amashr_refresh_token'];
export function purgeLegacyAuth(): void {
  try {
    for (const k of LEGACY_KEYS) localStorage.removeItem(k);
  } catch {
    /* storage unavailable */
  }
}

/**
 * Load the current session from the gateway (cookie → identity). Returns null
 * when unauthenticated. Captures the CSRF token for subsequent writes. The
 * backend re-verifies auth on every request, so this is display/UI state only.
 */
export async function fetchSession(): Promise<SessionUser | null> {
  const outcome = await fetchSessionOutcome();
  return outcome.status === 'authenticated' ? outcome.user : null;
}

/**
 * Like fetchSession, but distinguishes "definitely logged out" from "couldn't
 * tell". Session hydration must only clear persisted auth on the former.
 */
export async function fetchSessionOutcome(): Promise<SessionOutcome> {
  try {
    const res = await fetch(`${apiConfig.baseURL}/api/v1/auth/session`, { credentials: 'include' });
    if (res.status === 401 || res.status === 403) { csrf = null; return { status: 'unauthenticated' }; }
    if (!res.ok) return { status: 'error' }; // 5xx/gateway trouble: keep current state
    const data = await res.json();
    if (!data?.authenticated || !data.user) { csrf = null; return { status: 'unauthenticated' }; }
    csrf = data.csrfToken ?? null;
    return { status: 'authenticated', user: data.user as SessionUser };
  } catch {
    return { status: 'error' }; // network blip: keep current state
  }
}

/**
 * Adopt a freshly-minted access token (returned in a body by a not-yet-cookie
 * flow — MFA/social/onboarding/impersonation) INTO the httpOnly cookie via the
 * gateway (same-origin), then refresh the CSRF token. Returns true on success.
 */
export interface AdoptOutcome {
  ok: boolean;
  /** Gateway HTTP status, or 0 when the request never completed (network/CORS). */
  status: number;
  /** Short, non-sensitive failure reason. NEVER carries the token itself. */
  reason?: string;
}

/**
 * Adopt a token into the httpOnly session cookie, reporting WHY it failed.
 *
 * The boolean `adoptToken` below cannot distinguish "the gateway rejected the
 * token" from "the request never arrived", and every caller that ignored its
 * result then failed later for a different reason — which is exactly how an
 * impersonation handoff came to show one generic message for four distinct
 * causes. Use this wherever the failure is shown to a human or logged.
 */
export async function adoptTokenDetailed(accessToken: string): Promise<AdoptOutcome> {
  let res: Response;
  try {
    res = await fetch(`${apiConfig.baseURL}/api/v1/auth/session/adopt`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accessToken }),
    });
  } catch (e: any) {
    // fetch only rejects for network-level failures — DNS, TLS, blocked by CORS.
    return { ok: false, status: 0, reason: `request blocked (${e?.message || 'network or CORS'})` };
  }
  if (!res.ok) {
    const reason =
      res.status === 401 ? 'the gateway rejected the token (expired, wrong audience/issuer, or revoked)'
      : res.status === 403 ? 'origin not allowed by the gateway'
      : `gateway returned HTTP ${res.status}`;
    return { ok: false, status: res.status, reason };
  }
  const data = await res.json().catch(() => ({} as any));
  // A 2xx whose body is not the adopt response means we never reached the
  // gateway at all — typically an SPA host answering an unknown path with
  // index.html. Previously this counted as success and no cookie was set.
  if (data?.authenticated !== true) {
    return { ok: false, status: res.status, reason: 'no session in the response — check the configured API base URL' };
  }
  csrf = data?.csrfToken ?? csrf;
  return { ok: true, status: res.status };
}

export async function adoptToken(accessToken: string): Promise<boolean> {
  return (await adoptTokenDetailed(accessToken)).ok;
}

/**
 * Reusable axios request-interceptor: send the httpOnly session cookie and, on
 * state-changing methods, the session-bound CSRF token. No Authorization header —
 * the cookie is the credential. Replaces every `localStorage.getItem('accessToken')`
 * + `Bearer` interceptor across the app.
 */
export function attachCookieAuth<T extends AxiosRequestConfig>(config: T): T {
  config.withCredentials = true;
  const method = (config.method || 'get').toUpperCase();
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    config.headers = config.headers || {};
    (config.headers as Record<string, string>)['X-CSRF-Token'] = csrf || '';
  }
  return config;
}

/** For raw fetch() calls: the headers to spread onto a state-changing request. */
export function csrfHeaders(): Record<string, string> {
  return csrf ? { 'X-CSRF-Token': csrf } : {};
}
